import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiChevronDown, FiCompass, FiHome } from "react-icons/fi";
import styles from "../../styles/pages/Product/product-collection.module.css";
import { Product } from "../../interfaces/products/product";
import { getProducts } from "../../data-access/apis/products/productApi";
import SEO from "../../utils/seo/SEO";
import ProductCard from "../../component/products/ProductCard";

type CollectionKind =
  | "trending"
  | "best-rated"
  | "featured"
  | "best-sellers"
  | "latest"
  | "priority";

interface Props {
  kind: CollectionKind;
  title: string;
  eyebrow: string;
  description?: string;
}

const ProductCollection: React.FC<Props> = ({ kind, title, eyebrow, description }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState<"recommended" | "newest">("recommended");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);

      const filters =
        kind === "trending"
          ? { trending: true }
          : kind === "featured"
            ? { featured: true }
            : {};

      try {
        const data = await getProducts(filters);
        if (cancelled) return;

        setProducts(
          (Array.isArray(data) ? data : [])
            .filter((item) => item.isActive)
            .sort((a, b) => {
              if (kind === "latest") {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
              }
              return b.priorityScore - a.priorityScore;
            }),
        );
      } catch {
        if (!cancelled) {
          setProducts([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const visibleProducts = useMemo(() => {
    const copy = [...products];
    if (sort === "newest") {
      return copy.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return copy;
  }, [products, sort]);

  const emptyTitle = failed
    ? "We couldn't load this collection"
    : "This collection is being curated";
  const emptyText = failed
    ? "Please try again in a moment, or continue browsing the full catalogue."
    : "There are no active picks here yet. New products added from the admin panel will appear here automatically.";

  const routePath =
    kind === "best-sellers"
      ? "/best-sellers"
      : kind === "latest"
        ? "/latest-sellings"
        : kind === "priority"
          ? "/limited-offers"
          : `/${kind}`;

  return (
    <>
      <SEO
        title={`${title} | MyOnlineShopping`}
        description={description || `Explore ${title.toLowerCase()} on MyOnlineShopping and open product details before visiting Amazon.`}
        canonicalPath={routePath}
      />

      <main className={styles.page}>
        <div className={styles.container}>
          <div className={styles.topBar}>
            <button type="button" className={styles.back} onClick={() => navigate(-1)}>
              <FiArrowLeft /> Back
            </button>
            <div className={styles.breadcrumb}>
              <button type="button" onClick={() => navigate("/")}>Home</button>
              <span>/</span>
              {title}
            </div>
          </div>

          <header className={styles.hero}>
            <div>
              <p className={styles.eyebrow}>{eyebrow}</p>
              <h1>{title}</h1>
              <p>{description || "A clean, curated collection of active products. Open any card for images, product information and community feedback."}</p>
            </div>
            <div className={styles.heroStat}>
              <strong>{loading ? "—" : visibleProducts.length}</strong>
              <span>active products</span>
            </div>
          </header>

          <div className={styles.toolbar}>
            <span>{loading ? "Loading your picks…" : `${visibleProducts.length} products`}</span>
            {!loading && visibleProducts.length > 1 && (
              <label>
                Sort
                <span className={styles.selectWrap}>
                  <select value={sort} onChange={(e) => setSort(e.target.value as "recommended" | "newest")}>
                    <option value="recommended">Recommended</option>
                    <option value="newest">Newest</option>
                  </select>
                  <FiChevronDown />
                </span>
              </label>
            )}
          </div>

          {loading ? (
            <div className={styles.grid} aria-label="Loading products">
              {Array.from({ length: 8 }).map((_, index) => (
                <div className={styles.skeleton} key={index}>
                  <span />
                  <div><i /><i /><i /></div>
                </div>
              ))}
            </div>
          ) : visibleProducts.length ? (
            <div className={styles.grid}>
              {visibleProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          ) : (
            <section className={styles.empty}>
              <div className={styles.emptyIcon}><FiCompass /></div>
              <span className={styles.emptyKicker}>NOTHING TO SHOW YET</span>
              <h2>{emptyTitle}</h2>
              <p>{emptyText}</p>
              <div className={styles.emptyActions}>
                <button type="button" onClick={() => navigate("/products")}><FiCompass /> Browse all products</button>
                <button type="button" className={styles.secondaryEmptyAction} onClick={() => navigate("/")}><FiHome /> Back home</button>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default ProductCollection;
